import React, { useState, useEffect } from "react";
import { useStore } from "../../store/useStore";
import AuthLayout from "./AuthLayout";
import { ToggleLeft as Google, Eye, EyeOff, Loader2 } from "lucide-react";
import TermsModal from "../ui/custom/TermsModal";
import { getDatabase, push, ref, set } from "firebase/database";
import { Helmet } from "react-helmet";

interface SignUpProps {
  subscriptionAmount?: number;
}

function getClientId() {
  let id = localStorage.getItem("client_id");
  if (!id) {
    id = crypto.randomUUID(); // Built-in in modern browsers
    localStorage.setItem("client_id", id);
  }
  return id;
}

function getDeviceType(ua: string): "Mobile" | "Tablet" | "Desktop" {
  const uaLower = ua.toLowerCase();

  if (/mobile/i.test(uaLower)) {
    return "Mobile";
  }

  if (/ipad|tablet/i.test(uaLower)) {
    return "Tablet";
  }

  return "Desktop";
}

async function trackReferral(
  referralId: string,
  campaignId?: string,
  platform?: string
) {
  // Step 1: Get IP from backend
  const response = await fetch("/api/get-ip"); // your backend route
  const { ip } = await response.json();

  console.log(`Tracking referral click for ID: ${referralId}, IP: ${ip}`);

  // Step 2: Log to RTDB
  const db = getDatabase();
  const clickRef = push(ref(db, `referrals/${referralId}/clicks`));
  const data = {
    ip,
    userAgent: navigator.userAgent,
    visitedAt: Date.now(),
    device: getDeviceType(navigator.userAgent), // Determine device type
    UUID: getClientId(), // Store client ID for tracking
    campaignId,
    platform,
  };
  const clean = JSON.parse(JSON.stringify(data));
  await set(clickRef, clean);
}

async function trackAllClicks() {
  // Step 1: Get IP from backend
  const response = await fetch("/api/get-ip"); // your backend route
  const { ip } = await response.json();

  console.log(`Tracking click for, IP: ${ip}`);

  // Step 2: Log to RTDB
  const db = getDatabase();
  const clickRef = push(ref(db, `referrals/all/clicks`));
  await set(clickRef, {
    ip,
    userAgent: navigator.userAgent,
    visitedAt: Date.now(),
    device: getDeviceType(navigator.userAgent), // Determine device type
    UUID: getClientId(), // Store client ID for tracking
  });
}

const SignUp: React.FC<SignUpProps> = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [referralId, setReferralId] = useState<string | undefined>(undefined);
  const [campaignId, setCampaignId] = useState<string | undefined>(undefined);
  const [platform, setPlatform] = useState<string | undefined>(undefined);
  const [couponId, setCouponId] = useState<string | undefined>(undefined);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const getSubscriptionAmount = useStore(
    (state) => state.getSubscriptionAmount
  );
  const subscriptionAmount = useStore((state) => state.subscriptionAmount);
  const [amountToSubscribe, setAmountToSubscribe] =
    useState(subscriptionAmount);

  const [error, setError] = useState("");
  const signUp = useStore((state) => state.signUp);
  const loginWithGoogle = useStore((state) => state.loginWithGoogle);
  const isLoading = useStore((state) => state.isLoading);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      await signUp(
        email,
        password,
        firstName,
        lastName,
        referralId,
        termsAccepted,
        couponId,
        campaignId,
        platform
      );
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else {
        setError("Error creating account");
      }
    }
  };

  useEffect(() => {
    trackAllClicks();
  }, []);

  useEffect(() => {
    setAmountToSubscribe(subscriptionAmount);
  }, [subscriptionAmount]);

  useEffect(() => {
    if (!referralId) {
      if (new URLSearchParams(window.location.search).get("referral_id")) {
        const referralId = new URLSearchParams(window.location.search).get(
          "referral_id"
        );
        const campaignId =
          new URLSearchParams(window.location.search).get("campaign_id") ??
          undefined;
        const platform =
          new URLSearchParams(window.location.search).get("platform") ??
          undefined;
        setReferralId(referralId ?? undefined);
        setCampaignId(campaignId);
        setPlatform(platform);
        // console.log('Referral ID:', referralId, 'Campaign ID:', campaignId, 'Platform:', platform);
        // Track referral click if referralId is present
        if (referralId) trackReferral(referralId, campaignId, platform);
      }
    }
  }, [referralId, campaignId, platform]);

  useEffect(() => {
    if (!couponId) {
      if (new URLSearchParams(window.location.search).get("coupon_id")) {
        const couponId = new URLSearchParams(window.location.search).get(
          "coupon_id"
        );
        setCouponId(couponId ?? undefined);
        getSubscriptionAmount(couponId ?? undefined);
      }
    }
  }, [couponId]);

  const handleGoogleSignUp = (e: React.MouseEvent) => {
    e.preventDefault();
    setError("");

    loginWithGoogle().catch((err) => {
      if (err.code === "auth/popup-blocked") {
        setError("Please allow popups for this website to sign up with Google");
      } else if (err.code === "auth/email-already-in-use") {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else {
        setError("Google sign up failed");
      }
    });
  };

  return (
    <AuthLayout>
      <Helmet>
        <title>Ezyvibes SignUp</title>
        <link rel="canonical" href="https://app.ezyvibes.com/signup" />
      </Helmet>
      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* !error && subscriptionAmount !== undefined && subscriptionAmount < 10.99 && */}
        {!error && couponId && (
          <div className="bg-gray-700 dark:bg-gray-700 border border-white dark:border-white text-white dark:text-white px-4 py-3 rounded-lg text-md">
            <span className="block mt-2 text-white dark:text-white font-medium">
              Sign up now and enjoy our exclusive offer of £{amountToSubscribe}
              /month
            </span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
            {error.includes("already exists") && (
              <a
                href="/login"
                className="block mt-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
              >
                Go to Sign In
              </a>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative group">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              First Name
            </label>
            <div className="mt-1 relative">
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="hidden group-hover:block absolute z-10 top-0 left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap">
                Enter your legal first name
              </div>
            </div>
          </div>

          <div className="relative group">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Last Name
            </label>
            <div className="mt-1 relative">
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="hidden group-hover:block absolute z-10 top-0 left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap">
                Enter your legal last name
              </div>
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Email address
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Password
          </label>
          <div className="mt-1 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Confirm Password
          </label>
          <div className="mt-1 relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`appearance-none block w-full px-3 py-2 border ${
                confirmPassword == password
                  ? `border-gray-300 
              dark:border-gray-600 focus:ring-2 focus:ring-secondary focus:border-secondary`
                  : `border-red-300 dark:border-red-600 focus:ring-2 focus:ring-red-300 focus:border-red-600`
              } rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
              focus:outline-none bg-white 
              dark:bg-gray-700 text-gray-900 dark:text-white pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <label className="flex items-start space-x-2">
            <input
              type="checkbox"
              name="terms"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
              className="w-4 h-4 flex-shrink-0 accent-secondary cursor-pointer text-secondary focus:ring-secondary rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-gray-700 dark:text-gray-300 leading-snug">
              I have read and agree to the{" "}
              <a
                href="https://ezyvibes.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium text-secondary"
              >
                Terms of Use
              </a>
              .
            </span>
          </label>
        </div>

        <div className="mt-3">
          <label className="flex items-start space-x-2">
            <input
              type="checkbox"
              required
              checked={acceptedDisclaimer}
              onChange={() => setAcceptedDisclaimer(!acceptedDisclaimer)}
              className="w-4 h-4 flex-shrink-0 cursor-pointer text-secondary accent-secondary focus:ring-secondary rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-gray-700 dark:text-gray-300 leading-snug">
              I understand that Ezyvibes recipes and shopping lists may include
              allergens. I am solely responsible for checking all labels and
              ensuring food is safe for my dietary needs. See our full{" "}
              <a
                href="https://ezyvibes.com/disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium text-secondary"
              >
                Health Disclaimer
              </a>{" "}
              for details.
            </span>
          </label>
        </div>

        <div>
          <button
            type="submit"
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-secondary-700 hover:bg-bg-secondary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              firstName &&
              lastName &&
              email &&
              password &&
              confirmPassword == password &&
              termsAccepted &&
              acceptedDisclaimer
                ? isLoading
                : true
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Creating your account...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              Or continue with
            </span>
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={handleGoogleSignUp}
            className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
          >
            <Google className="h-5 w-5 mr-2" />
            Google
          </button>
        </div>

        <div className="text-sm text-center">
          <a
            type="button"
            href="/login"
            className="font-medium text-secondary hover:text-opacity-90"
          >
            Already have an account? Sign in
          </a>
        </div>
      </form>
      {showTerms && (
        <TermsModal onClose={() => setShowTerms(false)} isOpen={showTerms} />
      )}
    </AuthLayout>
  );
};

export default SignUp;
